// Several samples in one submission (POST /v1/jobs/bulk, backend/internal/api/bulk.go): up to MAX_BULK files, each a
// sample or an archive the backend unpacks (every file inside becomes its own run; runs are capped at MAX_BULK too).
//   MAX_BULK                      the backend's maxBulkJobs
//   isArchiveName(name)           the backend's archive extensions (it sniffs magic bytes only for extension-less files)
//   needsBulk(files)              more than one file, or one archive
//   makeBatch(files, current?) → Batch   the usable files, deduplicated, the first MAX_BULK kept; files past the cap are
//                                         counted, empty and oversize ones named
//   batchProfile(files)           one guest for the whole batch: linux-test only when every sample is a Linux type
import { MAX_SAMPLE_BYTES } from "./analyze";
import { inferProfile } from "./inferProfile";

export const MAX_BULK = 5;

const ARCHIVE_EXTS = [".tar.gz", ".tar.bz2", ".tar.xz", ".zip", ".7z", ".rar", ".tar", ".tgz", ".tbz", ".txz", ".gz", ".bz2", ".xz", ".cab", ".iso", ".wim"];

export const isArchiveName = (name: string): boolean => {
  const l = name.toLowerCase();
  return ARCHIVE_EXTS.some((e) => l.endsWith(e));
};

export const needsBulk = (files: readonly File[]): boolean => files.length > 1 || (files.length === 1 && isArchiveName(files[0].name));

export type Batch = { files: File[]; leftOut: number; skipped: { name: string; reason: "empty" | "too-large" }[] };

const keyOf = (f: File) => `${f.name}\0${f.size}\0${f.lastModified}`;

export function makeBatch(files: readonly File[], current?: Batch): Batch {
  const skipped: Batch["skipped"] = [...(current?.skipped ?? [])];
  const seen = new Set<string>();
  const usable: File[] = [];
  for (const f of [...(current?.files ?? []), ...files]) {
    if (f.size === 0) skipped.push({ name: f.name, reason: "empty" });
    else if (f.size > MAX_SAMPLE_BYTES) skipped.push({ name: f.name, reason: "too-large" });
    else if (!seen.has(keyOf(f))) {
      seen.add(keyOf(f));
      usable.push(f);
    }
  }
  return { files: usable.slice(0, MAX_BULK), leftOut: Math.max(0, usable.length - MAX_BULK) + (current?.leftOut ?? 0), skipped };
}

export function batchProfile(files: readonly File[]): string {
  const profiles = files.filter((f) => !isArchiveName(f.name)).map((f) => inferProfile(f, "file").profile);
  return profiles.length > 0 && profiles.every((p) => p === "linux-test") ? "linux-test" : "windows-11-23h2";
}
